import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  CreditCard, 
  Truck, 
  Share2, 
  Mail, 
  FileText, 
  MessageSquare,
  Settings as SettingsIcon,
  RefreshCw,
  Check
} from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import AdminPageHeader from '../../components/AdminPageHeader';
import { toast } from 'sonner';

const Settings = () => {
  const [updating, setUpdating] = useState(false);
  const [updateDone, setUpdateDone] = useState(false);

  const handleAppUpdate = async () => {
    setUpdating(true);
    try {
      if ('caches' in window) {
        const names = await caches.keys();
        await Promise.all(names.map((n) => caches.delete(n)));
      }
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        for (const reg of registrations) {
          await reg.update();
        }
      }
      setUpdateDone(true);
      toast.success('Aktualisierung wird geladen...');
      setTimeout(() => window.location.reload(true), 900);
    } catch (e) {
      toast.error('Aktualisierung fehlgeschlagen');
      setUpdating(false);
    }
  };

  const settingsCategories = [
    {
      title: 'Zahlungseinstellungen',
      description: 'Stripe, PayPal und Überweisung konfigurieren',
      icon: CreditCard,
      link: '/admin/payment-settings',
      color: 'indigo'
    },
    {
      title: 'Versandkosten',
      description: 'Versandarten und Kosten verwalten',
      icon: Truck,
      link: '/admin/shipping-settings',
      color: 'blue',
      badge: 'Neu'
    },
    {
      title: 'Social Media',
      description: 'Instagram, Facebook Verbindungen',
      icon: Share2,
      link: '/admin/social-settings',
      color: 'purple',
      badge: 'Neu'
    },
    {
      title: 'E-Mail Einstellungen',
      description: 'SMTP, Vorlagen und Benachrichtigungen',
      icon: Mail,
      link: '/admin/email-settings',
      color: 'green'
    },
    {
      title: 'E-Mail-Vorlagen Vorschau',
      description: 'Bestellbestätigung & Co. — Desktop und mobile Ansicht',
      icon: Mail,
      link: '/admin/email-templates',
      color: 'green',
      badge: 'Neu'
    },
    {
      title: 'Dokumenten-Einstellungen',
      description: 'Rechnungs- & Lieferschein-Layout, Logo, Absender-Adresse, Bankdaten + Live-PDF-Vorschau',
      icon: FileText,
      link: '/admin/document-settings',
      color: 'blue',
      badge: 'Editor'
    },
    {
      title: 'Newsletter',
      description: 'Newsletter-Verwaltung und Abonnenten',
      icon: MessageSquare,
      link: '/admin/newsletter-settings',
      color: 'cyan'
    },
    {
      title: 'Kontakt-Einstellungen',
      description: 'Kontaktformular und Informationen',
      icon: FileText,
      link: '/admin/contact-settings',
      color: 'orange',
      badge: 'Neu'
    }
  ];

  const getColorClasses = (color) => {
    const colors = {
      indigo: 'border-indigo-300 bg-indigo-50 hover:border-indigo-400',
      blue: 'border-blue-300 bg-blue-50 hover:border-blue-400',
      purple: 'border-purple-300 bg-purple-50 hover:border-purple-400',
      green: 'border-green-300 bg-green-50 hover:border-green-400',
      cyan: 'border-cyan-300 bg-cyan-50 hover:border-cyan-400',
      orange: 'border-orange-300 bg-orange-50 hover:border-orange-400'
    };
    return colors[color] || colors.indigo;
  };

  const getIconBgColor = (color) => {
    const colors = {
      indigo: 'bg-indigo-100',
      blue: 'bg-blue-100',
      purple: 'bg-purple-100',
      green: 'bg-green-100',
      cyan: 'bg-cyan-100',
      orange: 'bg-orange-100'
    };
    return colors[color] || colors.indigo;
  };

  const getIconColor = (color) => {
    const colors = {
      indigo: 'text-indigo-600',
      blue: 'text-blue-600',
      purple: 'text-purple-600',
      green: 'text-green-600',
      cyan: 'text-cyan-600',
      orange: 'text-orange-600'
    };
    return colors[color] || colors.indigo;
  };

  return (
    <div className="min-h-screen py-4 sm:py-6 lg:py-8 px-3 sm:px-4 md:px-6 lg:px-8" style={{ backgroundColor: '#F5EDE4' }}>
      <div className="max-w-7xl mx-auto">
        <AdminPageHeader 
          title="Einstellungen" 
          subtitle="Verwalten Sie alle Einstellungen Ihres Shops"
          icon={SettingsIcon}
        />

        {/* Section: Kategorien - Goldbraune Überschrift */}
        <div className="mb-4 p-2 px-3 flex items-center gap-2" style={{ backgroundColor: '#C4A084' }}>
          <div className="w-6 h-6 rounded flex items-center justify-center bg-[#F0FFF0]">
            <SettingsIcon className="h-4 w-4 text-[#60B060]" />
          </div>
          <h2 className="text-base font-semibold text-white">Einstellungs-Kategorien</h2>
        </div>

        {/* Settings Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {settingsCategories.map((category) => {
            const Icon = category.icon;
            return (
              <Link key={category.link} to={category.link} className="block">
                <Card 
                  className={`hover:shadow-lg transition-all cursor-pointer border-2 ${getColorClasses(category.color)} relative`}
                >
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-4">
                      <div className={`p-3 ${getIconBgColor(category.color)} rounded-lg flex-shrink-0`}>
                        <Icon className={`h-6 w-6 ${getIconColor(category.color)}`} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="font-semibold text-gray-900">{category.title}</p>
                          {category.badge && (
                            <span className="text-xs bg-green-500 text-white px-2 py-0.5 rounded-full">
                              {category.badge}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600">{category.description}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>

        {/* App Update Section */}
        <div className="mt-8">
          <div className="mb-4 p-2 px-3 flex items-center gap-2" style={{ backgroundColor: '#C4A084' }}>
            <div className="w-6 h-6 rounded flex items-center justify-center bg-[#F0FFF0]">
              <RefreshCw className="h-4 w-4 text-[#60B060]" />
            </div>
            <h2 className="text-base font-semibold text-white">App & Updates</h2>
          </div>

          <Card className="border-2 border-[#C4A084]">
            <CardContent className="pt-6">
              <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                <div className="p-3 bg-amber-100 rounded-lg flex-shrink-0">
                  <RefreshCw className={`h-6 w-6 text-amber-700 ${updating ? 'animate-spin' : ''}`} />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900 mb-1">App auf neueste Version aktualisieren</h3>
                  <p className="text-sm text-gray-600">
                    Lädt die neueste Version der App und leert den Cache. Nutzen Sie dies, falls Sie gerade Änderungen vorgenommen haben und diese sofort sehen wollen.
                  </p>
                </div>
                <Button
                  onClick={handleAppUpdate}
                  disabled={updating}
                  className="rounded-none bg-[#6D5A47] hover:bg-[#5A4937] text-white w-full sm:w-auto"
                  data-testid="settings-update-btn"
                >
                  {updateDone ? (
                    <><Check className="h-4 w-4 mr-2" /> Aktualisiert!</>
                  ) : (
                    <><RefreshCw className={`h-4 w-4 mr-2 ${updating ? 'animate-spin' : ''}`} /> {updating ? 'Aktualisiere...' : 'Jetzt aktualisieren'}</>
                  )}
                </Button>
              </div>
              <div className="mt-4 pt-4 border-t border-gray-200 text-xs text-gray-500">
                <strong>Automatisch:</strong> Die App prüft alle 10 Minuten im Hintergrund auf Updates. Kunden bekommen Updates ebenfalls automatisch.
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Info Box */}
        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="font-semibold text-blue-900 mb-2">💡 Tipp</h3>
          <p className="text-blue-800 text-sm">
            Änderungen an den Einstellungen werden sofort wirksam. Stelle sicher, dass alle Konfigurationen korrekt sind, bevor du sie speicherst.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Settings;
